import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  AggregateRequest,
  DashboardWidget,
  WidgetConfig,
  WIDGET_TYPES,
} from "../../../types/dashboard/DashboardConfig";
import { useDashboardAggregate } from "../../../hooks/useDashboardAggregate";
import { DRAWER_WIDTH, drawerHeaderSx, drawerTitleSx, drawerScrollAreaSx } from "../../components/shared/crudStyles";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import { LineChart } from "@mui/x-charts/LineChart";

/* ── Types ────────────────────────────────────────────────────────── */

export interface WidgetBuilderProps {
  open: boolean;
  onClose: () => void;
  onSave: (widget: DashboardWidget) => void;
  onDelete?: (widgetId: string) => void;
  editingWidget?: DashboardWidget;
  customerId?: string;
}

type AggregationType = "terms" | "dateHistogram" | "count" | "average";
type ChartType = "bar" | "pie" | "line" | "kpi";

/* ── Constants ────────────────────────────────────────────────────── */

const FIELDS_BY_AGGREGATION: Record<AggregationType, { value: string; label: string }[]> = {
  terms: [
    { value: "interactionType", label: "Interaction Type" },
    { value: "agentName", label: "Agent Name" },
    { value: "direction", label: "Direction" },
    { value: "tags", label: "Tags" },
    { value: "skill", label: "Skill" },
    { value: "group", label: "Group" },
    { value: "phoneNumber", label: "Phone Number" },
    { value: "dnis", label: "DNIS" },
  ],
  dateHistogram: [{ value: "startTime", label: "Start Time" }],
  count: [],
  average: [{ value: "durationMs", label: "Duration" }],
};

const CHART_TYPES_BY_AGGREGATION: Record<AggregationType, ChartType[]> = {
  terms: ["bar", "pie"],
  dateHistogram: ["bar", "line"],
  count: ["kpi"],
  average: ["kpi"],
};

const PREVIEW_COLORS = ["#4A7A9B", "#7B6B94", "#B89B5E", "#A66B73", "#5A8A72"];


/* ── Chart type icon SVGs ─────────────────────────────────────────── */

function BarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="2" y="10" width="4" height="8" rx="1" fill="currentColor" />
      <rect x="8" y="5" width="4" height="13" rx="1" fill="currentColor" />
      <rect x="14" y="2" width="4" height="16" rx="1" fill="currentColor" />
    </svg>
  );
}

function PieIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M10 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16z"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path d="M10 10V2a8 8 0 0 1 6.93 4L10 10z" fill="currentColor" />
    </svg>
  );
}

function LineIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <polyline
        points="2,15 6,8 10,12 14,4 18,7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function KpiIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <text
        x="10"
        y="15"
        textAnchor="middle"
        fontWeight="700"
        fontSize="14"
        fill="currentColor"
        fontFamily="sans-serif"
      >
        #
      </text>
    </svg>
  );
}

const CHART_ICONS: Record<ChartType, React.FC> = {
  bar: BarIcon,
  pie: PieIcon,
  line: LineIcon,
  kpi: KpiIcon,
};

/* ── Close icon ───────────────────────────────────────────────────── */

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M3 3l8 8M11 3l-8 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ── Section label ────────────────────────────────────────────────── */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        fontSize: "0.6rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "var(--text-muted)",
        marginBottom: "8px",
      }}
    >
      {children}
    </Box>
  );
}

/* ── Select-style dropdown ────────────────────────────────────────── */

interface StyledSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  label?: string;
}

function StyledSelect({ value, onChange, options, label }: StyledSelectProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {label && (
        <Box
          component="label"
          sx={{
            fontSize: "0.7rem",
            color: "var(--text-secondary)",
          }}
        >
          {label}
        </Box>
      )}
      <Box
        component="select"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
        sx={{
          appearance: "none",
          WebkitAppearance: "none",
          padding: "8px 32px 8px 12px",
          fontSize: "0.82rem",
          color: "var(--text-primary)",
          bgcolor: "var(--bg-raised)",
          border: "1px solid var(--border-mid)",
          borderRadius: "6px",
          outline: "none",
          cursor: "pointer",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%2394A3B8' stroke-width='1.5' stroke-linecap='round' fill='none'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 10px center",
          "&:hover": {
            borderColor: "var(--border-strong)",
          },
          "&:focus": {
            borderColor: "var(--accent)",
            boxShadow: "0 0 0 2px var(--accent-dim)",
          },
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Box>
    </Box>
  );
}

/* ── Filter input ────────────────────────────────────────────────── */

interface FilterInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}

function FilterInput({ label, value, onChange, type = "text", placeholder }: Readonly<FilterInputProps>) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Box
        component="label"
        sx={{
          fontSize: "0.7rem",
          color: "var(--text-secondary)",
        }}
      >
        {label}
      </Box>
      <Box
        component="input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        sx={{
          padding: "8px 12px",
          fontSize: "0.82rem",
          color: "var(--text-primary)",
          bgcolor: "var(--bg-raised)",
          border: "1px solid var(--border-mid)",
          borderRadius: "6px",
          outline: "none",
          colorScheme: "dark",
          "&::placeholder": {
            color: "var(--text-muted)",
          },
          "&:hover": {
            borderColor: "var(--border-strong)",
          },
          "&:focus": {
            borderColor: "var(--accent)",
            boxShadow: "0 0 0 2px var(--accent-dim)",
          },
        }}
      />
    </Box>
  );
}

/* ── Preview chart ───────────────────────────────────────────────── */

interface PreviewChartProps {
  chartType: ChartType;
  data: { labels: string[]; values: number[]; total: number };
}

function PreviewChart({ chartType, data }: Readonly<PreviewChartProps>) {
  if (chartType === "kpi") {
    return (
      <Box sx={{ fontSize: "2rem", fontWeight: 600, color: "var(--text-primary)" }}>
        {data.total.toLocaleString()}
      </Box>
    );
  }

  if (chartType === "pie") {
    const pieItems = data.labels.map((label: string, i: number) => ({
      id: i,
      label,
      value: data.values[i],
      color: PREVIEW_COLORS[i % PREVIEW_COLORS.length],
    }));
    return (
      <PieChart
        series={[{
          data: pieItems,
          innerRadius: "40%",
          paddingAngle: 1,
          cornerRadius: 2,
        }]}
        height={180}
        width={360}
        sx={{
          [`& .${pieArcLabelClasses.root}`]: { fill: "#fff", fontSize: 10, fontWeight: 600 },
        }}
        slotProps={{
          legend: {
            position: { vertical: "middle", horizontal: "right" },
            direction: "column",
            labelStyle: { fill: "var(--text-secondary)", fontSize: 10 },
          },
        }}
      />
    );
  }

  if (chartType === "line") {
    return (
      <LineChart
        xAxis={[{ scaleType: "band", data: data.labels }]}
        series={[{ data: data.values, color: PREVIEW_COLORS[0] }]}
        height={180}
        width={360}
        margin={{ left: 50, right: 8, top: 8, bottom: 24 }}
        sx={{
          "& .MuiChartsAxis-tickLabel": { fill: "var(--text-secondary) !important", fontSize: "0.65rem !important" },
          "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: "var(--border-mid) !important" },
        }}
        grid={{ horizontal: true }}
      />
    );
  }

  // Bar (default)
  return (
    <BarChart
      xAxis={[{ scaleType: "band", data: data.labels }]}
      series={[{ data: data.values, color: PREVIEW_COLORS[0] }]}
      height={180}
      width={360}
      margin={{ left: 50, right: 8, top: 8, bottom: 24 }}
      borderRadius={3}
      sx={{
        "& .MuiChartsAxis-tickLabel": { fill: "var(--text-secondary) !important", fontSize: "0.65rem !important" },
        "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: "var(--border-mid) !important" },
      }}
      grid={{ horizontal: true }}
    />
  );
}

/* ── Main component ───────────────────────────────────────────────── */

export default function WidgetBuilder({
  open,
  onClose,
  onSave,
  onDelete,
  editingWidget,
  customerId,
}: Readonly<WidgetBuilderProps>) {
  const { t } = useTranslation(["widgetBuilder", "common", "grid", "search"]);
  const isEditMode = !!editingWidget;

  // Translated options (rebuilt when language changes)
  const aggregationTypes = useMemo(() => [
    { value: "terms" as AggregationType, label: t("widgetBuilder:groupByCategory") },
    { value: "dateHistogram" as AggregationType, label: t("widgetBuilder:overTime") },
    { value: "count" as AggregationType, label: t("widgetBuilder:totalCount") },
    { value: "average" as AggregationType, label: t("widgetBuilder:average") },
  ], [t]);

  const fieldsByAggregation = useMemo((): Record<AggregationType, { value: string; label: string }[]> => ({
    terms: [
      { value: "interactionType", label: t("widgetBuilder:interactionType") },
      { value: "agentName", label: t("widgetBuilder:agentName") },
      { value: "direction", label: t("widgetBuilder:direction") },
      { value: "tags", label: t("widgetBuilder:tags") },
      { value: "skill", label: t("widgetBuilder:skill") },
      { value: "group", label: t("widgetBuilder:group") },
      { value: "phoneNumber", label: t("widgetBuilder:phoneNumber") },
      { value: "dnis", label: t("widgetBuilder:dnis") },
    ],
    dateHistogram: [{ value: "startTime", label: t("widgetBuilder:startTime") }],
    count: [],
    average: [{ value: "durationMs", label: t("grid:duration") }],
  }), [t]);

  const intervalOptions = useMemo(() => [
    { value: "1d", label: t("widgetBuilder:daily") },
    { value: "1M", label: t("widgetBuilder:monthly") },
    { value: "1y", label: t("widgetBuilder:yearly") },
  ], [t]);

  const allChartTypes = useMemo(() => [
    { value: "bar" as ChartType, label: t("widgetBuilder:bar") },
    { value: "pie" as ChartType, label: t("widgetBuilder:pie") },
    { value: "line" as ChartType, label: t("widgetBuilder:line") },
    { value: "kpi" as ChartType, label: t("widgetBuilder:kpi") },
  ], [t]);

  const directionOptions = useMemo(() => [
    { value: "", label: t("common:any") },
    { value: "in", label: t("search:inbound") },
    { value: "out", label: t("search:outbound") },
  ], [t]);

  const interactionTypeOptions = useMemo(() => [
    { value: "", label: t("common:any") },
    { value: "Audio", label: t("widgetBuilder:audio") },
    { value: "Video", label: t("widgetBuilder:video") },
    { value: "AudioAndVideo", label: t("search:audioAndVideo") },
    { value: "Chat", label: t("widgetBuilder:chat") },
    { value: "Email", label: t("widgetBuilder:email") },
  ], [t]);

  // Form state
  const [title, setTitle] = useState("");
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [aggregationType, setAggregationType] = useState<AggregationType>("terms");
  const [field, setField] = useState("interactionType");
  const [interval, setInterval] = useState("1M");
  const [size, setSize] = useState(10);
  const [titleError, setTitleError] = useState(false);

  // Filter state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterAgentName, setFilterAgentName] = useState("");
  const [filterDirection, setFilterDirection] = useState("");
  const [filterInteractionType, setFilterInteractionType] = useState("");
  const [filterSkill, setFilterSkill] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [filterTags, setFilterTags] = useState("");

  // Count active filters for the section header badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterFrom) count++;
    if (filterTo) count++;
    if (filterAgentName) count++;
    if (filterDirection) count++;
    if (filterInteractionType) count++;
    if (filterSkill) count++;
    if (filterGroup) count++;
    if (filterTags) count++;
    return count;
  }, [filterFrom, filterTo, filterAgentName, filterDirection, filterInteractionType, filterSkill, filterGroup, filterTags]);

  // Debounced preview request
  const [debouncedRequest, setDebouncedRequest] = useState<AggregateRequest | null>(null);

  useEffect(() => {
    if (!open) {
      setDebouncedRequest(null);
      return;
    }

    const timer = setTimeout(() => {
      const request: AggregateRequest = {
        aggregationType,
        field: field || "",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      if (aggregationType === "dateHistogram") {
        request.interval = interval;
      }
      if (aggregationType === "terms") {
        request.size = size;
      }

      // Include active filters
      if (filterFrom) request.from = filterFrom;
      if (filterTo) request.to = filterTo;
      if (filterAgentName) request.agentName = filterAgentName;
      if (filterDirection) request.direction = filterDirection;
      if (filterInteractionType) request.interactionType = filterInteractionType;
      if (filterSkill) request.skill = filterSkill;
      if (filterGroup) request.group = filterGroup;
      if (filterTags) {
        request.tags = filterTags.split(",").map((t) => t.trim()).filter(Boolean);
      }

      setDebouncedRequest(request);
    }, 500);

    return () => clearTimeout(timer);
  }, [open, aggregationType, field, interval, size, chartType, filterFrom, filterTo, filterAgentName, filterDirection, filterInteractionType, filterSkill, filterGroup, filterTags]);

  const { data: previewData, isLoading: previewLoading } = useDashboardAggregate(
    customerId,
    debouncedRequest,
    "preview"
  );

  // Reset form when drawer opens or editingWidget changes
  useEffect(() => {
    if (open) {
      if (editingWidget) {
        const config = editingWidget.config;
        setTitle(editingWidget.title);
        setChartType((config?.chartType as ChartType) ?? "bar");
        setAggregationType((config?.aggregationType as AggregationType) ?? "terms");
        setField(config?.field ?? "interactionType");
        setInterval(config?.interval ?? "1M");
        setSize(config?.size ?? 10);

        // Restore filter state
        const filters = config?.filters;
        setFilterFrom(filters?.from ?? "");
        setFilterTo(filters?.to ?? "");
        setFilterAgentName(filters?.agentName ?? "");
        setFilterDirection(filters?.direction ?? "");
        setFilterInteractionType(filters?.interactionType ?? "");
        setFilterSkill(filters?.skill ?? "");
        setFilterGroup(filters?.group ?? "");
        setFilterTags(filters?.tags?.join(", ") ?? "");
        // Auto-open filters section if any filters are active
        setFiltersOpen(
          !!(filters?.from || filters?.to || filters?.agentName || filters?.direction || filters?.interactionType || filters?.skill || filters?.group || (filters?.tags && filters.tags.length > 0))
        );
      } else {
        setTitle("");
        setChartType("bar");
        setAggregationType("terms");
        setField("interactionType");
        // eslint-disable-next-line no-implied-eval
        setInterval("1M");
        setSize(10);
        setFilterFrom("");
        setFilterTo("");
        setFilterAgentName("");
        setFilterDirection("");
        setFilterInteractionType("");
        setFilterSkill("");
        setFilterGroup("");
        setFilterTags("");
        setFiltersOpen(false);
      }
      setTitleError(false);
    }
  }, [open, editingWidget]);

  // Smart auto-fixing on aggregation type change
  const handleAggregationTypeChange = useCallback(
    (newType: AggregationType) => {
      setAggregationType(newType);

      // Auto-fix field
      const validFields = FIELDS_BY_AGGREGATION[newType];
      if (validFields.length > 0) {
        const currentFieldValid = validFields.some((f) => f.value === field);
        if (!currentFieldValid) {
          setField(validFields[0].value);
        }
      }

      // Auto-fix chart type
      const validChartTypes = CHART_TYPES_BY_AGGREGATION[newType];
      if (!validChartTypes.includes(chartType)) {
        setChartType(validChartTypes[0]);
      }
    },
    [field, chartType]
  );

  const handleSave = () => { // NOSONAR
    // Validate title
    if (!title.trim()) {
      setTitleError(true);
      return;
    }

    // Build config
    const config: WidgetConfig = {
      aggregationType,
      chartType,
    };

    // Include field only for types that use it
    if (aggregationType === "terms" || aggregationType === "dateHistogram" || aggregationType === "average") {
      config.field = field;
    }

    // Include interval only for dateHistogram
    if (aggregationType === "dateHistogram") {
      config.interval = interval;
    }

    // Include size only for terms
    if (aggregationType === "terms") {
      config.size = size;
    }

    // Include filters if any are active
    const hasFilters = filterFrom || filterTo || filterAgentName || filterDirection || filterInteractionType || filterSkill || filterGroup || filterTags;
    if (hasFilters) {
      config.filters = {};
      if (filterFrom) config.filters.from = filterFrom;
      if (filterTo) config.filters.to = filterTo;
      if (filterAgentName) config.filters.agentName = filterAgentName;
      if (filterDirection) config.filters.direction = filterDirection;
      if (filterInteractionType) config.filters.interactionType = filterInteractionType;
      if (filterSkill) config.filters.skill = filterSkill;
      if (filterGroup) config.filters.group = filterGroup;
      if (filterTags) {
        config.filters.tags = filterTags.split(",").map((t) => t.trim()).filter(Boolean);
      }
    }

    // Build widget
    const widget: DashboardWidget = {
      id: editingWidget?.id ?? crypto.randomUUID(),
      type: WIDGET_TYPES.CUSTOM_AGGREGATE,
      title: title.trim(),
      layout: editingWidget?.layout ?? {
        x: 0,
        y: 0,
        w: chartType === "kpi" ? 3 : 6,
        h: chartType === "kpi" ? 2 : 4,
        minW: 2,
        minH: 2,
      },
      config,
    };

    onSave(widget);
    onClose();
  };

  const handleDelete = () => {
    if (editingWidget && onDelete) {
      onDelete(editingWidget.id);
      onClose();
    }
  };

  const allowedChartTypes = CHART_TYPES_BY_AGGREGATION[aggregationType];
  const showFieldSelector = aggregationType === "terms" || aggregationType === "dateHistogram" || aggregationType === "average";
  const showIntervalSelector = aggregationType === "dateHistogram";
  const showSizeInput = aggregationType === "terms";

  const previewEmptyOrChart = !previewData || (previewData.labels.length === 0 && previewData.total === 0)
    ? <Box sx={{ color: "var(--text-muted)", fontSize: "0.78rem" }}>{t("widgetBuilder:noData")}</Box>
    : <PreviewChart chartType={chartType} data={previewData} />;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          bgcolor: "var(--bg-surface)",
          borderLeft: "1px solid var(--border-mid)",
          color: "var(--text-primary)",
          boxShadow: "-8px 0 24px rgba(0,0,0,0.2)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box sx={drawerHeaderSx}>
          <Box sx={drawerTitleSx}>
            {isEditMode ? t("widgetBuilder:editWidget") : t("widgetBuilder:createWidget")}
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close widget builder"
            sx={{
              color: "var(--text-muted)",
              "&:hover": {
                color: "var(--text-secondary)",
                bgcolor: "var(--bg-hover)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable content */}
        <Box sx={{ ...drawerScrollAreaSx, gap: "24px" }}>
          {/* Title section */}
          <Box>
            <SectionLabel>{t("widgetBuilder:title")}</SectionLabel>
            <TextField
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (e.target.value.trim()) setTitleError(false);
              }}
              placeholder={t("widgetBuilder:widgetTitle")}
              size="small"
              fullWidth
              error={titleError}
              helperText={titleError ? t("widgetBuilder:titleRequired") : undefined}
              inputProps={{ "aria-label": t("widgetBuilder:widgetTitle") }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "var(--bg-raised)",
                  color: "var(--text-primary)",
                  fontSize: "0.85rem",
                  "& fieldset": {
                    borderColor: "var(--border-mid)",
                  },
                  "&:hover fieldset": {
                    borderColor: "var(--border-strong)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "var(--accent)",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "var(--signal-rose)",
                },
              }}
            />
          </Box>

          {/* Chart type section */}
          <Box>
            <SectionLabel>{t("widgetBuilder:chartType")}</SectionLabel>
            <Box sx={{ display: "flex", gap: "8px" }}>
              {allChartTypes.map((ct) => {
                const Icon = CHART_ICONS[ct.value];
                const isAllowed = allowedChartTypes.includes(ct.value);
                const isSelected = chartType === ct.value;

                return (
                  <Tooltip key={ct.value} title={ct.label} arrow>
                    <Box
                      component="span"
                      sx={{ display: "inline-flex" }}
                    >
                      <IconButton
                        onClick={() => isAllowed && setChartType(ct.value)}
                        disabled={!isAllowed}
                        aria-label={ct.label}
                        aria-pressed={isSelected}
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "8px",
                          bgcolor: isSelected ? "var(--accent-dim)" : "var(--bg-raised)",
                          border: isSelected
                            ? "2px solid var(--accent)"
                            : "1px solid var(--border-mid)",
                          color: isSelected ? "var(--accent)" : "var(--text-secondary)",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            bgcolor: isSelected ? "var(--accent-dim)" : "var(--bg-hover)",
                          },
                          "&.Mui-disabled": {
                            opacity: 0.3,
                            color: "var(--text-muted)",
                            bgcolor: "var(--bg-raised)",
                            border: "1px solid var(--border-subtle)",
                          },
                        }}
                      >
                        <Icon />
                      </IconButton>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>

          {/* Data section */}
          <Box>
            <SectionLabel>{t("widgetBuilder:data")}</SectionLabel>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <StyledSelect
                label={t("widgetBuilder:aggregation")}
                value={aggregationType}
                onChange={(v) => handleAggregationTypeChange(v as AggregationType)}
                options={aggregationTypes}
              />

              {showFieldSelector && (
                <StyledSelect
                  label={t("widgetBuilder:field")}
                  value={field}
                  onChange={setField}
                  options={fieldsByAggregation[aggregationType]}
                />
              )}

              {showIntervalSelector && (
                <StyledSelect
                  label={t("widgetBuilder:interval")}
                  value={interval}
                  onChange={setInterval}
                  options={intervalOptions}
                />
              )}

              {showSizeInput && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <Box
                    component="label"
                    sx={{
                      fontSize: "0.7rem",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {t("widgetBuilder:topNResults")}
                  </Box>
                  <TextField
                    type="number"
                    value={size}
                    onChange={(e) => {
                      const val = Number.parseInt(e.target.value, 10);
                      if (!Number.isNaN(val) && val >= 1 && val <= 100) {
                        setSize(val);
                      }
                    }}
                    size="small"
                    inputProps={{
                      min: 1,
                      max: 100,
                      "aria-label": t("widgetBuilder:topNResults"),
                    }}
                    sx={{
                      width: 120,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "var(--bg-raised)",
                        color: "var(--text-primary)",
                        fontSize: "0.85rem",
                        "& fieldset": {
                          borderColor: "var(--border-mid)",
                        },
                        "&:hover fieldset": {
                          borderColor: "var(--border-strong)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "var(--accent)",
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Filters section */}
          <Box>
            <Box
              onClick={() => setFiltersOpen(!filtersOpen)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                userSelect: "none",
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--text-muted)",
                marginBottom: "8px",
                "&:hover": { color: "var(--text-secondary)" },
              }}
            >
              {t("widgetBuilder:filters")}{activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ""}
              <Box
                component="span"
                sx={{
                  fontSize: "0.5rem",
                  transition: "transform 0.2s ease",
                  transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▼
              </Box>
            </Box>
            <Collapse in={filtersOpen}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <FilterInput
                  label={t("widgetBuilder:dateFrom")}
                  type="datetime-local"
                  value={filterFrom}
                  onChange={setFilterFrom}
                />
                <FilterInput
                  label={t("widgetBuilder:dateTo")}
                  type="datetime-local"
                  value={filterTo}
                  onChange={setFilterTo}
                />
                <FilterInput
                  label={t("widgetBuilder:agentName")}
                  value={filterAgentName}
                  onChange={setFilterAgentName}
                  placeholder="e.g. John Smith"
                />
                <StyledSelect
                  label={t("widgetBuilder:direction")}
                  value={filterDirection}
                  onChange={setFilterDirection}
                  options={directionOptions}
                />
                <StyledSelect
                  label={t("widgetBuilder:interactionType")}
                  value={filterInteractionType}
                  onChange={setFilterInteractionType}
                  options={interactionTypeOptions}
                />
                <FilterInput
                  label={t("widgetBuilder:skill")}
                  value={filterSkill}
                  onChange={setFilterSkill}
                  placeholder="e.g. Sales"
                />
                <FilterInput
                  label={t("widgetBuilder:group")}
                  value={filterGroup}
                  onChange={setFilterGroup}
                  placeholder="e.g. Support Team"
                />
                <FilterInput
                  label={t("widgetBuilder:tags")}
                  value={filterTags}
                  onChange={setFilterTags}
                  placeholder="e.g. VIP, Escalation"
                />
              </Box>
            </Collapse>
          </Box>

          {/* Live Preview */}
          <Box>
            <SectionLabel>{t("widgetBuilder:preview")}</SectionLabel>
            <Box
              sx={{
                bgcolor: "var(--bg-raised)",
                border: "1px solid var(--border-mid)",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Widget title inside the preview card */}
              <Box
                sx={{
                  px: "14px",
                  pt: "12px",
                  pb: "4px",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                {title.trim() || t("widgetBuilder:newWidget")}
              </Box>
              <Box
                sx={{
                  height: 180,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
              {previewLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "var(--text-muted)" }} />
                  <Box component="span" sx={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                    {t("common:loading")}
                  </Box>
                </Box>
              ) : previewEmptyOrChart}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            padding: "16px 20px",
            borderTop: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Box>
            {isEditMode && onDelete && (
              <Button
                size="small"
                onClick={handleDelete}
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  color: "var(--signal-rose)",
                  "&:hover": {
                    bgcolor: "rgba(251,113,133,0.12)",
                  },
                }}
              >
                {t("widgetBuilder:deleteWidget")}
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
              size="small"
              onClick={onClose}
              sx={{
                fontSize: "0.75rem",
                textTransform: "none",
                color: "var(--text-secondary)",
                border: "1px solid var(--border-mid)",
                "&:hover": { bgcolor: "var(--bg-hover)" },
              }}
            >
              {t("common:cancel")}
            </Button>
            <Button
              size="small"
              onClick={handleSave}
              sx={{
                fontSize: "0.75rem",
                textTransform: "none",
                color: "var(--bg-deep)",
                bgcolor: "var(--accent)",
                "&:hover": { bgcolor: "var(--accent)", opacity: 0.9 },
              }}
            >
              {t("widgetBuilder:saveWidget")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
